import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  CanActivateFn,
  CanActivate,
} from '@angular/router';
import { KeycloakService } from '../services/keycloak/keycloak.service';
import { UsersService } from '../services/users/users.service';
import Bowser from 'bowser';
import { RouteStatus } from '../models/services/maintenance/routeStatus.model';
import { LocalStorageService } from '../services/localStorage/localStorage.service';
import { UserInfo } from '../models/user-info.model';
import { UserRightsHelper } from '../models/role.model';
import { RouteStatusService } from '../services/maintenance/route-status.service';
import { GraphService } from '../services/graph/graph.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CanAuthenticationGuard implements CanActivate {
  constructor(
    private keycloakService: KeycloakService,
    private router: Router,
    private routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    if (this.keycloakService.isAuthenticated()) {
      if (this.keycloakService.isTokenExpired()) {
        try {
          const refreshed = await this.keycloakService.refreshToken();
          if (!refreshed) {
            this.handleLogout();
            return false;
          }
        } catch (error) {
          console.error('Error refreshing token:', error);
          this.handleLogout();
          return false;
        }
      }

      try {
        const routeStatuses: RouteStatus[] = await lastValueFrom(
          this.routeStatusService.getRouteStatuses()
        );

        const routePath = '/' + this.getRoutePath(route);

        // Check if the routePath exists in routeStatuses
        const status = routeStatuses.find(status => status.url === routePath);

        if (status) {
          if (status.isDisabled) {
            this.router.navigate(['/errors/error-maintenance']); // Redirect to maintenance page
            return false;
          }
        }
      } catch (error) {
        console.error('Error fetching route statuses:', error);
        this.router.navigate(['/errors/error-maintenance']); // Redirect in case of error
        return false;
      }
      return true;
    } else {
      this.handleLogout();
      return false;
    }
  }

  private getRoutePath(route: ActivatedRouteSnapshot): string | undefined {
    // Adjust this method to extract the route path in a way that fits your route configuration
    let path = '';

    while (route.parent) {
      path = `${route.routeConfig?.path}/${path}`;
      route = route.parent;
    }

    return path.replace(/\/$/, ''); // Remove trailing slash if present
  }

  private handleLogout() {
    this._localStorageService.clear();
    this.keycloakService.login();
  }
}

@Injectable({
  providedIn: 'root',
})
export class UserForbiddenGuard implements CanActivate {
  constructor(
    private _router: Router,
    private _graphService: GraphService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const userEmail = route.params['email'];

    if (userEmail == null) {
      this._router.navigate(['/support']);
    }
    const canBeAccepted = await lastValueFrom(
      this._graphService.canUserBeAccepted(route.params['email'])
    );

    if (!canBeAccepted) {
      this._router.navigate(['/support']);
    }

    return true;
  }
}

/**
 * AuthGuardValidation checks if user exists in the local storage
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AuthGuardValidation: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const localStorageService = inject(LocalStorageService);
  if (localStorageService.getItem<UserInfo>('startUser')) {
    return true;
  }
  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AuthGuard checks if user exists in the userService
 * @returns boolean | UrlTree
 */
export const AuthGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userService = inject(UsersService);
  const currentUser = userService.currentUserValue;

  if (!currentUser) {
    return router.parseUrl('/login');
  } else if (currentUser.canChangeName || currentUser.canChangeNameStd) {
    return router.parseUrl('/name-change');
  } else if (currentUser.mustChangePwd) {
    return router.parseUrl('/password-change');
  }
  return true;
};

const chromeVersion = '40.0.0.0';
const safariVersion = '9.0.0.0';
const firefoxVersion = '35.0.0.0';
const edgeVersion = '12.0.0.0';

/**
 * BrowserGuard checks if the user navigator is supported
 * @returns boolean | UrlTree
 */
export const BrowserGuard: CanActivateFn = () => {
  const router = inject(Router);
  const browserVersion = Bowser.getParser(window.navigator.userAgent);
  const isVersionSupported =
    browserVersion.satisfies({
      chrome: `>=${chromeVersion}`,
      safari: `>=${safariVersion}`,
      firefox: `>=${firefoxVersion}`,
      edge: `>=${edgeVersion}`,
    }) === true;

  if (!isVersionSupported) {
    return router.parseUrl('/errors/error-browser');
  }
  return true;
};

/**
 * AccessRightRespInfo provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightRespInfo: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.hasRespInfoRights()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * BlockPageAuthGuard checks if the route is disabled (maintenance mode)
 * @param _route
 * @param state
 * @returns
 */
export const BlockPageAuthGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  if (_route.data && _route.data['disabled'] === true) {
    return router.createUrlTree(['/errors/error-maintenance'], {
      queryParams: { returnUrl: state.url },
    });
  }
  return true; // La navigation est autorisée
};

export const NameChangeAuthGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userService = inject(UsersService);
  const currentUser = userService.currentUserValue;
  if (!currentUser.canChangeName && !currentUser.canChangeNameStd) {
    return router.parseUrl('/login');
  }
  return true;
};

export const CanChangePasswordAuthGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userService = inject(UsersService);
  const currentUser = userService.currentUserValue;
  if (!currentUser?.mustChangePwd) {
    return router.parseUrl('/dashboard');
  }
  return true;
};

/**
 * AccessRightNewRespInfo provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightNewRespInfo: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.hasNewRespInfoRights()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightExchangeManagement provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightExchangeManagement: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.canManageExchange()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightUserManagement provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightUserManagement: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.canManageUsers()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightUserCreation provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightUserCreation: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.canCreateUsers()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightRoleManagement provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightRoleManagement: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  if (currentUser === null) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.canManageRoles()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightAdmin provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightProdTest: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);

  if (roleChecker.canAccessToTestFunc()) {
    return true;
  }

  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightAdmin provides access based on certain role names.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightAdmin: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.isSupportICT()) {
    return true;
  }

  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};

/**
 * AccessRightMaintenance provides access to maintenance based on certain role name.
 * @param _route unused
 * @param state State snapshot for redirect
 * @returns boolean | UrlTree
 */
export const AccessRightMaintenance: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(UsersService);

  const currentUser = userService.currentUserValue;
  const roleChecker = new UserRightsHelper(currentUser.roles, currentUser.orgs);
  if (roleChecker.isSupportEDUID()) {
    return true;
  }
  return router.createUrlTree(['/errors/error-403'], {
    queryParams: { returnUrl: state.url },
  });
};
